import React, { useState, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Paper,
  Divider,
  Alert,
  Chip,
  Tooltip,
  InputAdornment,
  Slider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import PublicIcon from '@mui/icons-material/Public';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { apiService } from '../../../services/apiService';
import { lookupErrorText } from '../../../utils/lookupError';
import ApproximateMatchNotice from '../../ApproximateMatchNotice';
import VariableStarInfo from '../../VariableStarInfo';
import TargetExposureConfig from './TargetExposureConfig';
import TargetCsvImportDialog from './TargetCsvImportDialog';
import TargetLibrarySelector from '../../library/LibraryTargetSelector';
import { astronomyService } from '../../../services/astronomyService';
import { computePositionFromElements } from '../../../utils/orbitalMechanics';

const UnifiedTargetStep = ({ control, errors, observatoryLocation, availableFilters, availableReadoutModes = [], defaultReadoutMode = null }) => {
  const { register, watch, setValue, clearErrors, setError } = useFormContext();
  const [lookupErrors, setLookupErrors] = useState({});
  const [lookupResults, setLookupResults] = useState({});
  const [isLookingUp, setIsLookingUp] = useState({});
  const [librarySelectorOpen, setLibrarySelectorOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Helper function to format time in both UTC and local timezone
  const formatTimeWithTimezones = (date) => {
    if (!date || !observatoryLocation) return { utc: '', local: '' };

    const dateObj = new Date(date);
    const timezone = observatoryLocation.timezone || 'UTC';

    // Format UTC time
    const utcTime = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    });

    // Format local time with timezone abbreviation
    // Handle invalid timezone names (e.g., Windows names like "Mountain Standard Time")
    let localTime;
    try {
      localTime = dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone,
        timeZoneName: 'short'
      });
    } catch (e) {
      // Fall back to UTC if timezone is invalid
      localTime = `${utcTime} (${timezone})`;
    }

    return {
      utc: `${utcTime} UTC`,
      local: localTime
    };
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'targets'
  });

  // Ensure at least one target exists
  useEffect(() => {
    if (fields.length === 0) {
      append({
        name: '',
        naif_id: '',   // #1409: consistency with addTarget (set by a spacecraft lookup)
        ra: '',
        dec: '',
        priority: 5,
        sequenceRepeatCount: 1,
        exposureConfigs: []
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register fields for validation
  useEffect(() => {
    fields.forEach((field, index) => {
      register(`targets.${index}.ra`, {
        required: 'RA is required',
        validate: value => {
          const num = parseFloat(value);
          return (!isNaN(num) && num >= 0 && num <= 360) || 'RA must be between 0 and 360 degrees';
        }
      });
      register(`targets.${index}.dec`, {
        required: 'Dec is required',
        validate: value => {
          const num = parseFloat(value);
          return (!isNaN(num) && num >= -90 && num <= 90) || 'Dec must be between -90 and 90 degrees';
        }
      });
    });
  }, [fields, register]);

  const convertRAToDecimal = (raString) => {
    if (!raString) return NaN;
    const parts = raString.split(/[:\s]+/);
    if (parts.length === 3) {
      const hours = parseFloat(parts[0]);
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      return (hours + minutes/60 + seconds/3600) * 15;
    }
    return parseFloat(raString);
  };

  const convertDecToDecimal = (decString) => {
    if (!decString) return NaN;
    const parts = decString.split(/[:\s]+/);
    if (parts.length === 3) {
      const sign = decString.includes('-') ? -1 : 1;
      const degrees = Math.abs(parseFloat(parts[0]));
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      return sign * (degrees + minutes/60 + seconds/3600);
    }
    return parseFloat(decString);
  };
  
  // Convert decimal to HMS/DMS format for display
  const formatRA = (decimal) => {
    if (!decimal && decimal !== 0) return '';
    const hours = decimal / 15;
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toFixed(2).padStart(5, '0')}`;
  };
  
  const formatDec = (decimal) => {
    if (!decimal && decimal !== 0) return '';
    const sign = decimal < 0 ? '-' : '+';
    const abs = Math.abs(decimal);
    const d = Math.floor(abs);
    const m = Math.floor((abs - d) * 60);
    const s = ((abs - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toFixed(2).padStart(5, '0')}`;
  };

  const handleTargetLookup = async (index) => {
    const targetName = watch(`targets.${index}.name`);
    if (!targetName) {
      setLookupErrors(prev => ({ ...prev, [index]: 'Please enter a target name first' }));
      return;
    }

    setIsLookingUp(prev => ({ ...prev, [index]: true }));
    setLookupErrors(prev => ({ ...prev, [index]: null }));
    
    try {
      const response = await apiService.lookupCoordinates(targetName);
      
      if (response.success && response.data) {
        const result = response.data;
        
        // Handle coordinates based on object type
        let computedRa, computedDec;
        
        if (result.coordinates.orbitalElements) {
          // For solar system objects, compute current position from orbital elements
          try {
            const currentPosition = computePositionFromElements(result.coordinates.orbitalElements);
            computedRa = currentPosition.ra;
            computedDec = currentPosition.dec;
            
            setValue(`targets.${index}.ra`, computedRa.toFixed(6), { 
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            });
            setValue(`targets.${index}.dec`, computedDec.toFixed(6), { 
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            });
            console.log(`Computed current position for ${targetName}: RA=${computedRa.toFixed(6)}, Dec=${computedDec.toFixed(6)}`);
          } catch (error) {
            console.error('Failed to compute position from orbital elements:', error);
            // Leave fields empty if computation fails
          }
        } else {
          // For fixed objects, use the provided coordinates
          computedRa = result.coordinates.ra;
          computedDec = result.coordinates.dec;
          
          setValue(`targets.${index}.ra`, computedRa.toFixed(6), { 
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          });
          setValue(`targets.${index}.dec`, computedDec.toFixed(6), {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          });
        }

        // #1409: keep the hidden naif_id field in sync with the lookup result. For a spacecraft, store the
        // NAIF id (this is what makes scheduling/dispatch re-query the OBSERVER ephemeris) and enrich the
        // visible name to the resolved human name (e.g. "RST" → "Roman Space Telescope"), which stays
        // human-readable for FITS OBJECT / archive paths / reports. Clear it for any non-spacecraft result
        // so a prior spacecraft lookup doesn't linger if the user re-resolves to a fixed target.
        const scNaifId = (result.coordinates.objectType === 'spacecraft' && result.coordinates.naifId) || '';
        setValue(`targets.${index}.naif_id`, scNaifId, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        if (scNaifId && result.resolvedName) {
          setValue(`targets.${index}.name`, result.resolvedName, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          });
        }

        // Calculate rise/transit/set times if we have observatory location
        let riseSetData = null;
        if (observatoryLocation?.latitude && observatoryLocation?.longitude) {
          // For moving objects with orbital elements, use the moving object calculation
          if (result.coordinates.orbitalElements) {
            console.log('Calculating rise/set times for moving object with orbital elements');
            riseSetData = astronomyService.calculateRiseSetTimesMoving(
              result.coordinates.orbitalElements,
              observatoryLocation.latitude,
              observatoryLocation.longitude,
              observatoryLocation.altitude_meters || 0,
              new Date(),
              30, // minimum altitude
              computePositionFromElements // pass the function to compute positions
            );
          } else if (computedRa !== undefined && computedDec !== undefined) {
            // For fixed objects, use static coordinate calculation
            console.log(`Calculating rise/set times for fixed object RA=${computedRa}, Dec=${computedDec}`);
            riseSetData = astronomyService.calculateRiseSetTimes(
              computedRa,
              computedDec,
              observatoryLocation.latitude,
              observatoryLocation.longitude,
              observatoryLocation.altitude_meters || 0,
              new Date()
            );
          }
          console.log('Rise/set data calculated:', riseSetData);
        }
        
        // Create enhanced result with formatted coordinates and rise/set times
        // For moving objects with orbital elements, use computed RA/Dec instead of server's 0,0
        const displayRa = computedRa !== undefined ? computedRa : result.coordinates.ra;
        const displayDec = computedDec !== undefined ? computedDec : result.coordinates.dec;

        const enhancedResult = {
          ...result,
          coordinates: {
            ...result.coordinates,
            ra: displayRa,
            dec: displayDec,
            ra_hms: formatRA(displayRa),
            dec_dms: formatDec(displayDec)
          },
          riseSetTimes: riseSetData
        };
        
        setLookupResults(prev => ({ ...prev, [index]: enhancedResult }));
      } else {
        setLookupErrors(prev => ({ ...prev, [index]: 'Target not found' }));
      }
    } catch (error) {
      setLookupErrors(prev => ({ ...prev, [index]: lookupErrorText(error, 'Lookup failed') }));
    } finally {
      setIsLookingUp(prev => ({ ...prev, [index]: false }));
    }
  };

  const addTarget = () => {
    append({
      name: '',
      naif_id: '',   // #1409: set by a spacecraft lookup; empty for normal targets
      ra: '',
      dec: '',
      priority: 5,
      sequenceRepeatCount: 1,
      exposureConfigs: [{
        filter: 'L',
        exposureTime: 300,
        exposureCount: 1,
        binning: '1',
        gain: null,
        offset: null
      }]
    });
  };

  const handleLibrarySelect = (target) => {
    // If we have an empty first target, replace it
    if (fields.length === 1 && !watch('targets.0.name') && !watch('targets.0.ra')) {
      setValue('targets.0.name', target.name);
      setValue('targets.0.ra', target.ra);
      setValue('targets.0.dec', target.dec);
      setValue('targets.0.priority', target.priority);
      setValue('targets.0.sequenceRepeatCount', target.sequenceRepeatCount);
      if (target.exposureConfigs && target.exposureConfigs.length > 0) {
        setValue('targets.0.exposureConfigs', target.exposureConfigs);
      }
    } else {
      // Otherwise append as new target
      append(target);
    }

    // Increment usage count for the saved target
    if (target.savedTargetId) {
      apiService.incrementTargetUsage(target.savedTargetId).catch(err => {
        console.error('Failed to increment target usage:', err);
      });
    }
  };

  const handleCsvImport = (importedTargets) => {
    if (!importedTargets || importedTargets.length === 0) return;
    // If the only target is the initial empty one, replace it with the first
    // imported target (mirrors handleLibrarySelect), then append the remainder.
    const firstIsEmpty =
      fields.length === 1 && !watch('targets.0.name') && !watch('targets.0.ra');
    const toAppend = importedTargets;
    if (firstIsEmpty) {
      const [first, ...rest] = importedTargets;
      setValue('targets.0.name', first.name);
      setValue('targets.0.ra', first.ra);
      setValue('targets.0.dec', first.dec);
      setValue('targets.0.priority', first.priority);
      setValue('targets.0.sequenceRepeatCount', first.sequenceRepeatCount);
      setValue('targets.0.exposureConfigs', first.exposureConfigs);
      rest.forEach((t) => append(t));
      return;
    }
    toAppend.forEach((t) => append(t));
  };

  const duplicateTarget = (index) => {
    const target = fields[index];
    append({
      ...target,
      name: target.name + ' (copy)'
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Target Information & Exposure Plan
      </Typography>
      
      <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              {fields.length} target{fields.length !== 1 ? 's' : ''} configured
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* TEMPORARILY DISABLED: Target Library
              <Button
                variant="outlined"
                startIcon={<StarIcon />}
                onClick={() => setLibrarySelectorOpen(true)}
                size="small"
                color="secondary"
              >
                From Library
              </Button>
              */}
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setCsvImportOpen(true)}
                size="small"
              >
                Import Target CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addTarget}
                size="small"
              >
                Add Target
              </Button>
            </Box>
          </Box>

          {fields.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No targets added yet. Click "Add Target" to create one manually.
            </Alert>
          )}

          {fields.map((field, index) => (
            <Paper key={field.id} sx={{ p: 3, mb: 3 }} variant="outlined">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Target {index + 1}
                  {watch(`targets.${index}.name`) && (
                    <Chip 
                      label={watch(`targets.${index}.name`)} 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Box>
                  <Tooltip title="Duplicate this target">
                    <IconButton 
                      size="small" 
                      onClick={() => duplicateTarget(index)}
                      color="primary"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove this target">
                    <IconButton 
                      size="small" 
                      onClick={() => remove(index)}
                      color="error"
                      disabled={fields.length === 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Target Name"
                    placeholder="Enter target name (e.g., M31, NGC 2237, 433 Eros)"
                    {...register(`targets.${index}.name`, {
                      required: 'Target name is required'
                    })}
                    error={!!errors.targets?.[index]?.name}
                    helperText={errors.targets?.[index]?.name?.message || 'Enter a target name and click the search icon to look up coordinates'}
                    // Float the label when a value is set programmatically (CSV import /
                    // library select); an uncontrolled field won't shrink on its own, so
                    // the label would overlap the imported name. undefined = MUI default.
                    InputLabelProps={{ shrink: Boolean(watch(`targets.${index}.name`)) || undefined }}
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Look up coordinates">
                          <IconButton
                            onClick={() => handleTargetLookup(index)}
                            disabled={isLookingUp[index] || !watch(`targets.${index}.name`)}
                            edge="end"
                            color={isLookingUp[index] ? 'primary' : 'default'}
                          >
                            <SearchIcon />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>

                {lookupErrors[index] && (
                  <Grid item xs={12}>
                    <Alert severity="error" onClose={() => setLookupErrors(prev => ({ ...prev, [index]: null }))}>
                      {lookupErrors[index]}
                    </Alert>
                  </Grid>
                )}

                {lookupResults[index] && (
                  <Grid item xs={12}>
                    <ApproximateMatchNotice result={lookupResults[index]} query={lookupResults[index].objectName} sx={{ mb: 2 }} />
                    <Alert
                      severity={lookupResults[index].approximateMatch ? 'info' : 'success'}
                      onClose={() => setLookupResults(prev => ({ ...prev, [index]: null }))}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Coordinates found via {lookupResults[index].service}
                      </Typography>
                      <VariableStarInfo variableStar={lookupResults[index].coordinates?.variableStar} sx={{ mb: 1 }} />
                      <Box sx={{ mt: 1 }}>

                        {/* #1409: spacecraft banner — a SEPARATE block (NOT the orbitalElements ternary,
                            whose truthy branch dereferences orbitalElements.a and would crash here). */}
                        {lookupResults[index].coordinates.objectType === 'spacecraft' && (
                          <Typography variant="body2" color="warning.main" gutterBottom>
                            <strong>⚠️ Moving Object (Spacecraft) — position computed at observation time</strong>
                            {lookupResults[index].coordinates.naifId
                              ? ` · tracked by NAIF id ${lookupResults[index].coordinates.naifId}`
                              : ''}
                            <br />
                            <span style={{ fontWeight: 'normal' }}>
                              Preview position is approximate (geocentric); the telescope is pointed using a
                              fresh topocentric ephemeris computed at observation time.
                            </span>
                          </Typography>
                        )}

                        {/* Show orbital elements for solar system objects */}
                        {lookupResults[index].coordinates.orbitalElements ? (
                          <>
                            <Typography variant="body2" color="warning.main" gutterBottom>
                              <strong>⚠️ Moving Object - Position will be computed at observation time</strong>
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>Orbital Elements (Epoch JD {lookupResults[index].coordinates.orbitalElements.epoch}):</strong>
                            </Typography>
                            <Box sx={{ ml: 2 }}>
                              <Typography variant="body2">
                                <strong>Semi-major axis (a):</strong> {lookupResults[index].coordinates.orbitalElements.a.toFixed(6)} AU
                              </Typography>
                              <Typography variant="body2">
                                <strong>Eccentricity (e):</strong> {lookupResults[index].coordinates.orbitalElements.e.toFixed(6)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Inclination (i):</strong> {lookupResults[index].coordinates.orbitalElements.i.toFixed(4)}°
                              </Typography>
                              <Typography variant="body2">
                                <strong>Arg. of perihelion (ω):</strong> {lookupResults[index].coordinates.orbitalElements.omega.toFixed(4)}°
                              </Typography>
                              <Typography variant="body2">
                                <strong>Ascending node (Ω):</strong> {lookupResults[index].coordinates.orbitalElements.node.toFixed(4)}°
                              </Typography>
                              <Typography variant="body2">
                                <strong>Mean anomaly (M):</strong> {lookupResults[index].coordinates.orbitalElements.M.toFixed(4)}°
                              </Typography>
                              {lookupResults[index].coordinates.orbitalElements.P && (
                                <Typography variant="body2">
                                  <strong>Period:</strong> {lookupResults[index].coordinates.orbitalElements.P.toFixed(3)} years
                                </Typography>
                              )}
                              {lookupResults[index].coordinates.orbitalElements.H !== undefined && (
                                <Typography variant="body2">
                                  <strong>Absolute magnitude (H):</strong> {lookupResults[index].coordinates.orbitalElements.H.toFixed(1)}
                                </Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              <strong>RA:</strong> {lookupResults[index].coordinates.ra_hms} = {lookupResults[index].coordinates.ra.toFixed(6)}°
                            </Typography>
                            <Typography variant="body2">
                              <strong>Dec:</strong> {lookupResults[index].coordinates.dec_dms} = {lookupResults[index].coordinates.dec.toFixed(6)}°
                            </Typography>
                          </>
                        )}
                        
                        {/* Display object properties as chips */}
                        <Box sx={{ mt: 1 }}>
                          {lookupResults[index].coordinates.objectType && (
                            <Chip 
                              size="small" 
                              label={lookupResults[index].coordinates.objectType} 
                              color="primary" 
                              sx={{ mt: 1, mr: 1 }}
                              icon={<PublicIcon />}
                            />
                          )}
                          {lookupResults[index].coordinates.magnitude !== undefined && (
                            <Chip 
                              size="small" 
                              label={`Magnitude: ${lookupResults[index].coordinates.magnitude.toFixed(2)}`} 
                              color="secondary" 
                              sx={{ mt: 1, mr: 1 }}
                            />
                          )}
                          {lookupResults[index].coordinates.spectralType && (
                            <Chip 
                              size="small" 
                              label={`Spectral Type: ${lookupResults[index].coordinates.spectralType}`} 
                              color="info" 
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                        
                        {lookupResults[index].coordinates.properMotion && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>Proper Motion:</strong> RA: {lookupResults[index].coordinates.properMotion.ra.toFixed(3)} mas/yr, Dec: {lookupResults[index].coordinates.properMotion.dec.toFixed(3)} mas/yr
                            </Typography>
                          </Box>
                        )}
                        
                        {lookupResults[index].riseSetTimes && (
                          <Box sx={{ mt: 1 }}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              Today's Visibility (at 30° altitude):
                            </Typography>
                            {lookupResults[index].riseSetTimes.isCircumpolar ? (
                              <Typography variant="body2">
                                Circumpolar (always visible)
                              </Typography>
                            ) : lookupResults[index].riseSetTimes.neverRises ? (
                              <Typography variant="body2">
                                Never rises at this location
                              </Typography>
                            ) : (
                              <Grid container spacing={3} sx={{ pl: 1 }}>
                                {lookupResults[index].riseSetTimes.riseTime && (
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Box sx={{ minWidth: 150 }}>
                                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                        Rises to 30°:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {formatTimeWithTimezones(lookupResults[index].riseSetTimes.riseTime).utc}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {formatTimeWithTimezones(lookupResults[index].riseSetTimes.riseTime).local}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {lookupResults[index].riseSetTimes.transitTime && (
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Box sx={{ minWidth: 150 }}>
                                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                        Transit:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {formatTimeWithTimezones(lookupResults[index].riseSetTimes.transitTime).utc}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {formatTimeWithTimezones(lookupResults[index].riseSetTimes.transitTime).local}
                                      </Typography>
                                      {lookupResults[index].riseSetTimes.maxAltitude && (
                                        <Typography variant="caption" color="text.secondary">
                                          (Max Alt: {lookupResults[index].riseSetTimes.maxAltitude.toFixed(1)}°)
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                                {lookupResults[index].riseSetTimes.setTime && (
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Box sx={{ minWidth: 150 }}>
                                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                        Sets below 30°:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {formatTimeWithTimezones(lookupResults[index].riseSetTimes.setTime).utc}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {formatTimeWithTimezones(lookupResults[index].riseSetTimes.setTime).local}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            )}
                          </Box>
                        )}
                        
                        {lookupResults[index].coordinates.epoch && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                            Epoch: {lookupResults[index].coordinates.epoch}
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextField
                      fullWidth
                      label="Right Ascension (RA)"
                      value={watch(`targets.${index}.ra`) || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        clearErrors(`targets.${index}.ra`);
                        setValue(`targets.${index}.ra`, value, {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }}
                      onBlur={() => {
                        const value = watch(`targets.${index}.ra`);
                        if (value) {
                          const num = parseFloat(value);
                          if (isNaN(num) || num < 0 || num > 360) {
                            setError(`targets.${index}.ra`, {
                              type: 'manual',
                              message: 'RA must be between 0 and 360 degrees'
                            });
                          }
                        }
                      }}
                      error={!!errors.targets?.[index]?.ra}
                      helperText={errors.targets?.[index]?.ra?.message || 'Format: HH:MM:SS or decimal degrees'}
                      placeholder="83.633 or 05:34:31.94"
                      InputLabelProps={{
                        shrink: true
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Right Ascension can be entered as HH:MM:SS (e.g., 12:34:56) or decimal degrees (e.g., 185.725)">
                              <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {watch(`targets.${index}.ra`) && !errors.targets?.[index]?.ra && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 0.5 }}>
                        {(() => {
                          const raValue = watch(`targets.${index}.ra`);
                          const decimal = parseFloat(raValue);
                          if (!isNaN(decimal)) {
                            // If input looks like decimal, show sexagesimal
                            if (!raValue.includes(':')) {
                              return `= ${formatRA(decimal)}`;
                            }
                            // If input is sexagesimal, convert and show decimal
                            const convertedDec = convertRAToDecimal(raValue);
                            return `= ${convertedDec.toFixed(6)}°`;
                          }
                          return '';
                        })()}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextField
                      fullWidth
                      label="Declination (Dec)"
                      value={watch(`targets.${index}.dec`) || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        clearErrors(`targets.${index}.dec`);
                        setValue(`targets.${index}.dec`, value, {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }}
                      onBlur={() => {
                        const value = watch(`targets.${index}.dec`);
                        if (value) {
                          const num = parseFloat(value);
                          if (isNaN(num) || num < -90 || num > 90) {
                            setError(`targets.${index}.dec`, {
                              type: 'manual',
                              message: 'Dec must be between -90 and 90 degrees'
                            });
                          }
                        }
                      }}
                      error={!!errors.targets?.[index]?.dec}
                      helperText={errors.targets?.[index]?.dec?.message || 'Format: ±DD:MM:SS or decimal degrees'}
                      placeholder="-5.391 or -05:23:28"
                      InputLabelProps={{
                        shrink: true
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Declination can be entered as ±DD:MM:SS (e.g., +41:16:09 or -23:45:00) or decimal degrees">
                              <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {watch(`targets.${index}.dec`) && !errors.targets?.[index]?.dec && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 0.5 }}>
                        {(() => {
                          const decValue = watch(`targets.${index}.dec`);
                          const decimal = parseFloat(decValue);
                          if (!isNaN(decimal)) {
                            // If input looks like decimal, show sexagesimal
                            if (!decValue.includes(':')) {
                              return `= ${formatDec(decimal)}`;
                            }
                            // If input is sexagesimal, convert and show decimal
                            const convertedDec = convertDecToDecimal(decValue);
                            return `= ${convertedDec.toFixed(6)}°`;
                          }
                          return '';
                        })()}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>
                      Priority: {watch(`targets.${index}.priority`) || 5} (10 = highest, 1 = lowest)
                    </Typography>
                    <Slider
                      value={watch(`targets.${index}.priority`) || 5}
                      onChange={(event, newValue) => {
                        setValue(`targets.${index}.priority`, newValue);
                      }}
                      aria-labelledby={`priority-slider-${index}`}
                      valueLabelDisplay="auto"
                      step={1}
                      marks
                      min={1}
                      max={10}
                      color={(watch(`targets.${index}.priority`) || 5) >= 8 ? 'error' : (watch(`targets.${index}.priority`) || 5) >= 5 ? 'warning' : 'primary'}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        Low Priority
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        High Priority
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Exposure Configuration for {watch(`targets.${index}.name`) || `Target ${index + 1}`}
                    </Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12}>
                  <TargetExposureConfig
                    control={control}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    targetIndex={index}
                    availableFilters={availableFilters}
                    availableReadoutModes={availableReadoutModes}
                    defaultReadoutMode={defaultReadoutMode}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Paper sx={{ p: 2, mt: 2, backgroundColor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Global Sequence Settings
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Global Repeat Count"
              {...register('globalRepeatCount', {
                valueAsNumber: true,
                min: 1
              })}
              inputProps={{ min: 1 }}
              helperText={fields.length > 1 
                ? "Number of times to cycle through all targets" 
                : "Number of times to repeat the entire observation"}
              defaultValue={1}
              sx={{ mt: 1 }}
            />
          </Paper>
          
          {fields.length > 1 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Multi-Target Execution:</strong> Targets will be observed sequentially in the order listed above.
                {watch('globalRepeatCount') > 1 && ` The entire sequence will be repeated ${watch('globalRepeatCount')} times.`}
              </Typography>
            </Alert>
          )}
      </Box>

      {/* Target Library Selector Dialog */}
      <TargetLibrarySelector
        open={librarySelectorOpen}
        onClose={() => setLibrarySelectorOpen(false)}
        onSelect={handleLibrarySelect}
      />

      {/* CSV target-list import */}
      <TargetCsvImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImport={handleCsvImport}
        availableFilters={availableFilters}
      />
    </Box>
  );
};

export default UnifiedTargetStep;